<template>
  <q-card>
    <div
      v-if="!noHeader"
      :class="[
        'title',
        'text-center',
        $q.dark.mode ? 'bg-grey-9' : 'bg-grey-3',
      ]"
    >
      <span
        :class="
          type.includes('sell')
            ? 'text-red-6 text-weight-bold'
            : 'text-secondary text-weight-medium'
        "
      >
        {{ name }}
        <q-icon
          class="q-ml-xs"
          size="xs"
          :name="
            type.includes('sell')
              ? 'sell'
              : type.includes('buy')
              ? 'shopping_cart'
              : ''
          "
        />
      </span>
    </div>

    <q-separator size="xs" />

    <div style="max-height: 350px; overflow-y: scroll">
      <div v-if="data && (weekly || isThisSavedModel)">
        <q-list class="" style="width: 100%">
          <template v-for="(symbolList, date) of sortedData" :key="date">
            <q-expansion-item
              expand-separator
              :label="intraday ? convertDate(date) : date"
              dense
              icon="date_range"
              :default-opened="true"
              :header-class="
                $q.dark.mode
                  ? ''
                  : 'text-teal-5 bg-grey-1 text-weight-medium text-subtitle2 q-py-none q-my-none'
              "
              :expand-icon-class="$q.dark.mode ? '' : 'text-teal-6'"
            >
              <q-separator color="blue-2" />
              <div class="q-pa-sm row">
                <template v-for="symbol in symbolList" :key="symbol">
                  <q-btn
                    dense
                    v-if="allStocks"
                    style="padding: 0px 6px 0px 6px"
                    @click="setActiveSymbol(symbol, date)"
                    :size="btnSizeSm ? 'sm' : 'md'"
                    :class="btnSizeSm ? 'q-ma-xs' : 'q-ma-sm'"
                    :outline="!(activeSymbol === symbol && activeDate === date)"
                    :color="$q.dark.mode ? 'grey-7' : 'blue-8'"
                    :label="symbol"
                  >
                    <q-tooltip>{{
                      allStocks.find((stock) => stock.symbol === symbol)?.name
                    }}</q-tooltip>
                  </q-btn>
                </template>
              </div>
            </q-expansion-item>
          </template>
        </q-list>
      </div>
      <div v-else-if="data && !weekly && !isThisSavedModel">
        <template v-for="symbol in data" :key="symbol">
          <q-btn
            dense
            v-if="allStocks"
            style="padding: 0px 6px 0px 6px"
            @click="setActiveSymbol(symbol)"
            :class="btnSizeSm ? 'q-ma-xs' : 'q-ma-sm'"
            :size="btnSizeSm ? 'sm' : 'md'"
            :outline="activeSymbol !== symbol"
            :color="$q.dark.mode ? 'grey-7' : 'blue-8'"
            :label="symbol"
          >
            <q-tooltip>{{
              allStocks.find((stock) => stock.symbol === symbol)?.name
            }}</q-tooltip>
          </q-btn>
        </template>
        <template v-if="addButton">
          <q-btn
            @click="addStock"
            class="q-ma-sm"
            outline
            color="primary"
            label="Add Stock"
          />
        </template>
      </div>
      <div v-else-if="loading">
        <div class="q-pt-sm q-pb-md text-center">
          <i>Loading...</i>
        </div>
      </div>
      <div class="text-center q-pt-sm q-pb-md" v-else>
        <i>No {{ name }}s Found!</i>
      </div>
    </div>
  </q-card>
</template>

<script>
import { ref, watch, computed, onMounted } from "vue";
import moment from "moment";
import { useStore } from "vuex";

export default {
  name: "ListCard",
  props: {
    isThisSavedModel: Boolean,
    name: { type: String, required: true, default: "No Title" },
    addButton: { type: Boolean, default: false },
    data: { type: Array, required: true, default: () => ["No Data."] },
    weekly: Boolean,
    intraday: Boolean,
    type: { type: String, required: true, default: "" },
    loading: { type: Boolean, required: true, default: true },
    activeSymbol: String,
    activeDate: String,
    btnSizeSm: Boolean,
    btnSizeMd: Boolean,
    noHeader: { type: Boolean, default: false },
  },
  emits: ["clickSymbol", "clickSymbolandDate"],
  setup(props, context) {
    const $store = useStore();

    const allStocks = ref([]);
    const weeklyData = ref({});
    const sortedData = ref({});

    const setActiveSymbol = (cSymbol, cDate) => {
      context.emit("clickSymbol", cSymbol);
      context.emit("clickSymbolandDate", cSymbol, cDate);
    };

    onMounted(() => {
      if ($store.state.core.allStocks) {
        allStocks.value = $store.state.core.allStocks;
      } else {
        $store.dispatch("core/getAllStocks").then(() => {
          allStocks.value = $store.state.core.allStocks;
        });
      }
    });

    const processData = (data) => {
      return data.reduce((acc, curr) => {
        if (!acc[curr.date]) {
          acc[curr.date] = [];
        }

        acc[curr.date].push(curr.symbol);
        return acc;
      }, {});
    };

    const convertDate = (date) => {
      const newDate = moment(date).format("YYYY-MM-DD, hh A");
      return newDate;
    };

    weeklyData.value =
      props.weekly || props.isThisSavedModel ? processData(props.data) : {};

    Object.keys(weeklyData.value).forEach((date) => {
      sortedData.value[date] = weeklyData.value[date].sort();
    });

    setActiveSymbol(props.activeSymbol, props.activeDate);

    watch(
      [() => props.isThisSavedModel, () => props.weekly, () => props.data],
      ([isSavedModel, isWeekly, newData], [wasSavedModel, wasWeekly]) => {
        if (isSavedModel || isWeekly) {
          weeklyData.value = processData(newData);
          sortedData.value = {};
          Object.keys(weeklyData.value).forEach((date) => {
            sortedData.value[date] = weeklyData.value[date].sort();
          });
        }

        setActiveSymbol(props.activeSymbol, props.activeDate);
      }
    );

    return {
      allStocks,
      weeklyData,
      setActiveSymbol,
      sortedData,
      convertDate,
    };
  },
};
</script>
